// Package imports:
import React, { useEffect, useMemo, useState } from 'react';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
// Component imports:
import Alert from '../../../ui-elements/Alert/Alert';
import Label from '../../../ui-elements/Label/Label';
import Loading from '../../../ui-elements/Loading/Loading';
import DisplayBox from '../../DisplayBox/DisplayBox';
import Button from '../../../ui-elements/Button/Button';
import DropSelect from '../../../ui-elements/DropSelect/DropSelect';
// Service imports:
import { GET_KELDAN_API_URL } from '../../../services/config';
import { getTableDate, insertDash } from '../../../services/utils';
import { usePageCounter } from '../../../services/hooks';
import { useBuyingProcessUrl } from '../../../services/buyhook';
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { Fetched } from '../../../types/Types';
import { IKeldanApiResponse, OwnedReport } from '../../../types/KeldanTypes';
import { LegalAdvert, LegalSearchModel } from '../../../types/LegalTypes';
import { ICompanyViewModel } from '../../../types/CompanyTypes';

const CompanyLegalLarge: React.FC<ICompanyViewModel> = ({
    id
}) => {
    const [data, setData] = useState<Fetched<LegalSearchModel>>();
    const [ownedData, setOwnedData] = useState<Fetched<OwnedReport[]>>(null);

    const { currentPageIndex, totalPages, goToNextPage, goToPreviousPage, setTotalPages, resetPageCounter } = usePageCounter();
    const [ itemsToShow, setItemsToShow ] = useState(10);
    const [ itemCount, setItemCount ] = useState(0);

    // Pagination memos.
    const isNextPageDisabled = useMemo(() => {
        if (totalPages === null) return true;
        return ((currentPageIndex + 1) >= totalPages)
    }, [ currentPageIndex, totalPages]);

    const isPrevPageDisabled = useMemo(() => {
        return (currentPageIndex === 0)
    }, [ currentPageIndex ]);

    const fetchData = async () => {
        try {
            const url = `${GET_KELDAN_API_URL()}/Company/GetCompanyLegal?id=${id}&limit=${10000}`
            const headers = {
                'Content-Type': 'application/json; charset=utf-8'
            };
            
            const response = await fetch(url, {
                method: 'GET',
                headers
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                }
                const responseBody: IKeldanApiResponse<LegalSearchModel> = await response.json();
                if (responseBody.success) {
                    if(responseBody.result){
                        setData(responseBody.result);
                        setItemCount(responseBody.result.count);
                        setTotalPages(responseBody.result.count, itemsToShow);
                    } 
                    else if (responseBody.message === "'Ekkert fannst") setData(null);
                } else {
                    const { message } = responseBody;
                    if (message) {
                        setData(new Error(message));
                    } else {
                        setData(new Error(ErrorMessages.ErrorInRequest));
                    }
                }
            } else {
                setData(new Error(ErrorMessages.RequestFailed));
            }
        } catch (e) {
            setData(new Error(ErrorMessages.OtherShort));
        }
    }
    const fetchOwnedData = async () => {
        try {
            const url = `${GET_KELDAN_API_URL()}/Company/GetCompanyOwnedLegal/${id}`;
            const headers = {
                'Content-Type': 'application/json; charset=utf-8'
            };
            
            const response = await fetch(url, {
                method: 'GET',
                headers
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                }
                const responseBody: IKeldanApiResponse<OwnedReport[]> = await response.json();
                if (responseBody.success) {
                    if(responseBody.result) setOwnedData(responseBody.result);
                } else {
                    const { message } = responseBody;
                    if (message) {
                        setOwnedData(new Error(message));
                    } else {
                        setOwnedData(new Error(ErrorMessages.ErrorInRequest));
                    }
                }
            } else {
                setOwnedData(new Error(ErrorMessages.RequestFailed));
            }
        } catch (e) {
            setOwnedData(new Error(ErrorMessages.OtherShort));
        }
    }
    useEffect(() => {
        fetchData();
        fetchOwnedData();
    }, []);

    useEffect(() => {
        setTotalPages(itemCount, itemsToShow);
    }, [itemCount, itemsToShow]);

    useEffect(() => {
        resetPageCounter();
    }, [itemsToShow]);

    const { tryPurchaseItem, isItemLoading, ModalsAndToasts } = useBuyingProcessUrl(
        (item: LegalAdvert, modalReference: string) => {
            const url = `${GET_KELDAN_API_URL()}/Fyrirtaeki/Logbirtingar-Kaupa/${id}?adNumber=${item.advertNumber}&reference=${modalReference}`;
            return url;
        },
        () => {
            window.setTimeout(() => {
                fetchOwnedData();
            }, 5*1000);
        },
        'Lögbirtingablaðinu'
    );

    const checkIfOwned = (adNumber: string) => {
        if(ownedData !== null && !(ownedData instanceof Error))
            for(let i = 0; i < ownedData.length ; i++){
                if(ownedData[i].tokens.includes(adNumber)) return ownedData[i].balls
            }
        return 0;
    }

    return (
        <DisplayBox 
            className='KCL_company-legal KCL_company-legal_large'
            title="Lögbirtingar"
            asideComponent={<Label labelType='origin' text='LÖGB.' fullText='Lögbirtingablaðið' />}
        >
            {data instanceof Error
                ? <Alert type='error' headText={data.message} />
                : data === undefined
                ? <Loading />
                : data === null || itemCount === 0
                ? <Alert type='info' headText={`Leit eftir ${insertDash(id ?? '')} skilaði engum niðurstöðum`} />
                : <div>
                    <div className='middle-section'>
                        <span className='lighterText'>{`Leit eftir ${insertDash(id ?? '')} skilaði ${data.count} ${(data.count === 1) ? 'niðurstöðu': 'niðurstöðum'}.`}</span>
                        <div className="flex-container">
                            <span className='lighterText'>Sýna:</span>
                            <DropSelect
                                options={[{
                                    label: '10 á síðu',
                                    value: '10'
                                }, {
                                    label: '50 á síðu',
                                    value: '50'
                                }, {
                                    label: '100 á síðu',
                                    value: '100'
                                },{
                                    label: 'Allt á síðu',
                                    value: '10000'
                                }]}
                                defaultValue={itemsToShow.toString()}
                                size='sm'
                                onChange={(option) => { if (typeof option === 'string') setItemsToShow(parseInt(option)); resetPageCounter(); }}
                            />
                        </div>
                    </div>
                    {
                        data.ads?.slice(currentPageIndex * itemsToShow, (currentPageIndex + 1) * itemsToShow).map((item, index) => (
                            <div key={index} className='company-legal-line'>
                                <div>
                                    <span className='company-legal-line-date'>{`${getTableDate(item.published, "DD/MM/YYYY", ".")}`} </span>
                                    <span className='company-legal-line-text'>{`${item.type}`} </span>
                                </div>
                                {
                                    //check if owned report
                                    (checkIfOwned(item.advertNumber.toString()) !== 0)
                                    ? <Button
                                        size='sm'
                                        buttonType='secondary'
                                        anchorProps={{href: `/Fyrirtaeki/Logbirtingar-Skoda/${id}?eventId=${checkIfOwned(item.advertNumber.toString())}`, target: '_blank'}}
                                        >Skoða</Button>
                                    : <Button
                                        size='sm'
                                        buttonType='buy'
                                        showLoader={isItemLoading(item)}
                                        onClick={() => tryPurchaseItem(item, 'confirm-modal-with-reference')}
                                    >
                                        Kaupa
                                    </Button>
                                }
                            </div>
                    ))}
                    <div className="pagination_wrapper">
                        <FontAwesomeIcon  
                            className={cx('pagination_angle', {'disabled': isPrevPageDisabled})} 
                            icon={faAngleLeft}
                            onClick={isPrevPageDisabled ? undefined : goToPreviousPage}
                        />
                        <span className="pagination_text">síða {currentPageIndex + 1} af {totalPages ?? '...'}</span>
                        <FontAwesomeIcon  
                            className={cx('pagination_angle', {'disabled': isNextPageDisabled})} 
                            icon={faAngleRight}
                            onClick={isNextPageDisabled ? undefined : goToNextPage}/>
                    </div>
                </div>
            }
            <ModalsAndToasts />
        </DisplayBox>
    );
}

export default CompanyLegalLarge;